/**
 * 選択中の素材（人物=AI俳優を除く）を1枚のシート画像にまとめてダウンロードする。
 *
 * 設計方針 (2026-06-16 改訂): ロゴシート ({@link renderBrandLogoSheetBlob}) と同様に、
 * 動画生成 AI がカタログ風の装飾 (オフホワイト地・金のアクセント罫・カードの影/角丸・
 * フッター) を誤認識するケースがあったため、装飾を排して「上部に大きなタイトル
 * (商品名/ブランド名) + 黒い細枠の画像枠 + 枠下に素材名キャプション」という素朴な
 * グリッドにする。素材名 (GenerationAsset.name) は AI に各画像の役割を伝えるため枠下に焼き込む。
 *  - 背景は白 (非透過出力)
 *  - 各素材はクロップ・アスペクト比変更をせず contain でフィットさせ、黒い細枠は
 *    画像の実寸にフィットさせて描く (画像と枠線の間にレターボックスの隙間を作らない)
 *  - 素材名を持つ素材が 1 つでもあれば、全セルの下端に同じ高さのキャプション領域を確保する
 */
import { fetchFacePrivacySanitizedMaterial, fetchMaterialSignedUrl } from '../../../features/materials/api';
import type { GenerationAsset } from '../state/useGenerationState';

/** 解像度スケール（見た目を保ったまま出力解像度を上げる係数。3 で 3600x3600px 出力） */
const SCALE = 3;
const CANVAS_SIZE = 1200 * SCALE;
/** キャンバス外周の余白(px) */
const OUTER_PADDING = 32 * SCALE;
/** セル間の間隔(px) */
const GUTTER = 26 * SCALE;

/** シート地色（白。非透過出力） */
const BG_COLOR = '#ffffff';
/** 画像枠を区切る黒い細枠 (論理 1px) */
const CELL_BORDER_COLOR = '#000000';
const CELL_BORDER_WIDTH = 1 * SCALE;

/** 上部ブランド名のフォントサイズ(px、商品名より小さめ) */
const BRAND_FONT_SIZE = 24 * SCALE;
/** 上部商品名のフォントサイズ(px、2 行に収まりやすいよう控えめ) */
const PRODUCT_FONT_SIZE = 28 * SCALE;
/** ブランド名の文字色（グレー） */
const BRAND_COLOR = '#9b9b9b';
/** 商品名の文字色（ほぼ黒） */
const TITLE_COLOR = '#1a1a1a';

/** 画像下に焼き込む素材名キャプションのフォントサイズ(px) */
const CAPTION_FONT_SIZE = 18 * SCALE;
/** キャプション領域の高さ(px、画像枠の下に確保。最大 2 行ぶん) */
const CAPTION_H = 52 * SCALE;
/** キャプション（素材名）の文字色 */
const CAPTION_COLOR = '#1a1a1a';

/**
 * テキストを maxWidth に収まる複数行に折り返す（日本語は単語境界が無いため 1 文字単位で改行）。
 * fillText に maxWidth を渡すと字形が横方向に圧縮されて縦長に見えるため、その代わりに使う。
 * maxLines を超える分は末尾を省略記号にまとめて切り詰める。
 */
function wrapLines(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  maxLines: number,
): string[] {
  const lines: string[] = [];
  let current = '';
  for (const ch of text) {
    const candidate = current + ch;
    if (current && ctx.measureText(candidate).width > maxWidth) {
      lines.push(current);
      current = ch;
      if (lines.length === maxLines - 1) break;
    } else {
      current = candidate;
    }
  }
  // 残り（最終行）。打ち切った場合は残テキスト全体を最終行に詰めて省略記号で切り詰める
  const consumed = lines.join('').length;
  let last = text.slice(consumed);
  if (ctx.measureText(last).width > maxWidth) {
    const ellipsis = '…';
    while (last.length > 1 && ctx.measureText(last + ellipsis).width > maxWidth) {
      last = last.slice(0, -1);
    }
    last += ellipsis;
  }
  lines.push(last);
  return lines;
}

/** 高解像度の元画像URLを取得（署名URL優先、失敗時は sourceUrl/thumbnailUrl にフォールバック） */
async function resolveHighResUrl(asset: GenerationAsset): Promise<string | null> {
  try {
    return await fetchMaterialSignedUrl(asset.id, asset.type === 'temporary');
  } catch {
    return asset.sourceUrl || asset.thumbnailUrl || null;
  }
}

/**
 * 素材に永続紐付けされたサニタイズ画像（実在人物フィルタ対策の覆面化/線画の隠しファイル）
 * の URL を返す。
 * 紐付けが無い / 画像素材でない / 照会失敗時は null（呼び出し側は元画像のまま合成する）。
 */
async function resolveFacePrivacySanitizedUrl(asset: GenerationAsset): Promise<string | null> {
  const contentType = asset.contentType ?? '';
  if (contentType.startsWith('video/') || contentType.startsWith('audio/')) return null;
  try {
    const twin = await fetchFacePrivacySanitizedMaterial(asset.type, asset.id);
    if (!twin) return null;
    // サニタイズ画像は temporary_materials の隠しファイル。署名URLを取り直し、
    // 取得できなければ行の preview_url にフォールバックする
    const signed = await fetchMaterialSignedUrl(twin.material.id, true).catch(() => null);
    return signed || twin.material.preview_url || null;
  } catch {
    return null;
  }
}

/** URLを fetch して Image 要素にロードする（CORS taint 回避のため blob 経由） */
async function loadImage(url: string): Promise<{ img: HTMLImageElement; objectUrl: string } | null> {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    if (!blob.type.startsWith('image/')) return null;
    const objectUrl = URL.createObjectURL(blob);
    const img = new Image();
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('image load failed'));
      img.src = objectUrl;
    });
    return { img, objectUrl };
  } catch {
    return null;
  }
}

/**
 * 素材をタイル画像（マス目グリッド）にまとめた PNG blob を生成する。
 * ダウンロード（{@link downloadSelectedAssetsTile}）と、動画生成への添付
 * （一時素材アップロード用 File 化）の両方から再利用する共通コア。
 *
 * options.preferFacePrivacySanitized を立てると、覆面化画像（実在人物フィルタ対策で
 * 作成済みの隠しファイル）が紐付いている素材はそちらを使って合成する。動画生成へ
 * 添付する統合画像は個別素材と違いバックエンドが中身を差し替えられないため、
 * 合成時点で顔なし画像に置き換えておく必要がある。
 * @returns 生成した blob・配置できた素材枚数・そのうち覆面化画像で合成した枚数。
 *          配置できる素材が無ければ null。
 */
export async function renderSelectedAssetsTileBlob(
  assets: GenerationAsset[],
  meta?: { productName?: string; brandName?: string; footerLabel?: string },
  options?: { preferFacePrivacySanitized?: boolean },
): Promise<{ blob: Blob; count: number; sanitizedCount: number } | null> {
  const objectUrls: string[] = [];
  const cells: { img: HTMLImageElement; name: string }[] = [];
  let sanitizedCount = 0;

  // 覆面化画像の紐付け照会は素材ごとに 1 リクエストかかるため、逐次ロードの前に
  // まとめて並列解決しておく（照会失敗した素材は元画像のまま合成する）。
  const sanitizedUrlByAssetId = new Map<string, string>();
  if (options?.preferFacePrivacySanitized) {
    const resolved = await Promise.all(assets.map((a) => resolveFacePrivacySanitizedUrl(a)));
    resolved.forEach((url, i) => {
      if (url) sanitizedUrlByAssetId.set(assets[i].id, url);
    });
  }

  for (const asset of assets) {
    const sanitizedUrl = sanitizedUrlByAssetId.get(asset.id);
    let usedSanitized = !!sanitizedUrl;
    let loaded = sanitizedUrl ? await loadImage(sanitizedUrl) : null;
    if (!loaded) {
      // 覆面化画像が読めなかった場合も元画像で合成を続ける（枚数が欠けるのを防ぐ）
      usedSanitized = false;
      const url = await resolveHighResUrl(asset);
      if (!url) continue;
      loaded = await loadImage(url);
    }
    if (!loaded) continue;
    if (usedSanitized) sanitizedCount += 1;
    cells.push({ img: loaded.img, name: (asset.name ?? '').trim() });
    objectUrls.push(loaded.objectUrl);
  }

  const cleanup = () => objectUrls.forEach((u) => URL.revokeObjectURL(u));

  if (cells.length === 0) {
    cleanup();
    return null;
  }

  const canvas = document.createElement('canvas');
  canvas.width = CANVAS_SIZE;
  canvas.height = CANVAS_SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    cleanup();
    return null;
  }

  // 背景: 白（非透過）
  ctx.fillStyle = BG_COLOR;
  ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);

  const innerLeft = OUTER_PADDING;
  const innerW = CANVAS_SIZE - OUTER_PADDING * 2;

  // === ヘッダー: ブランド名（小・グレー）+ 商品名（大・太字・黒）を左寄せで描画（装飾なし）===
  const brand = (meta?.brandName ?? '').trim();
  const title = (meta?.productName ?? '').trim();
  const hasHeader = !!(brand || title);
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  let textY = OUTER_PADDING;
  if (brand) {
    ctx.fillStyle = BRAND_COLOR;
    ctx.font = `600 ${BRAND_FONT_SIZE}px sans-serif`;
    // 長くても字形を圧縮せず、自然な幅で折り返す（ブランド名は最大 2 行）
    for (const line of wrapLines(ctx, `ブランド名: ${brand}`, innerW, 2)) {
      ctx.fillText(line, innerLeft, textY);
      textY += BRAND_FONT_SIZE * 1.4;
    }
    textY += BRAND_FONT_SIZE * 0.2;
  }
  if (title) {
    ctx.fillStyle = TITLE_COLOR;
    ctx.font = `bold ${PRODUCT_FONT_SIZE}px sans-serif`;
    // 長い商品名は字形を圧縮せず、自然な幅で折り返す（最大 2 行・超過分は省略）
    for (const line of wrapLines(ctx, `商品名: ${title}`, innerW, 2)) {
      ctx.fillText(line, innerLeft, textY);
      textY += PRODUCT_FONT_SIZE * 1.25;
    }
  }

  // グリッド上端: ヘッダーがあればその下から、無ければ外周余白から開始
  const gridTop = hasHeader ? Math.round(textY + GUTTER) : OUTER_PADDING;

  // === グリッド: 各素材を「黒い細枠の画像枠 + 枠下に素材名キャプション」で配置 ===
  // 素材名を持つ素材が 1 つでもあれば、各セルの下端にキャプション領域を確保する
  // (枠の高さを揃えるため、名前の有無に関わらず同じ高さを引く)。
  const hasAnyName = cells.some((c) => c.name);
  const captionH = hasAnyName ? CAPTION_H : 0;
  const n = cells.length;
  const cols = Math.ceil(Math.sqrt(n));
  const rows = Math.ceil(n / cols);

  // 利用可能領域。グリッドを領域全体に引き伸ばすと、セルが画像（おおむね正方形）
  // より大きくなり、画像周りのレターボックス余白（=見かけ上の隙間）が過大になる。
  // これを避けるため画像領域を正方形に揃えてセル寸法を内容に合わせ、グリッド全体を
  // 中央寄せする（余った余白は四辺に分配する）。
  const availW = innerW;
  const availH = CANVAS_SIZE - gridTop - OUTER_PADDING;
  const cellByW = (availW - GUTTER * (cols - 1)) / cols;
  const cellByH = (availH - GUTTER * (rows - 1) - rows * captionH) / rows;
  // 画像を収める領域（正方形。幅・高さの制約のうち小さい方に合わせる）
  const imageAreaH = Math.max(1, Math.min(cellByW, cellByH));
  const cellW = imageAreaH;
  const cellH = imageAreaH + captionH;

  // グリッド全体を利用可能領域の中央に配置
  const gridBlockW = cols * cellW + (cols - 1) * GUTTER;
  const gridBlockH = rows * cellH + (rows - 1) * GUTTER;
  const gridLeft = innerLeft + Math.max(0, (availW - gridBlockW) / 2);
  const gridTopCentered = gridTop + Math.max(0, (availH - gridBlockH) / 2);

  cells.forEach(({ img, name }, i) => {
    const col = i % cols;
    const row = Math.floor(i / cols);
    const cx = gridLeft + col * (cellW + GUTTER);
    const cy = gridTopCentered + row * (cellH + GUTTER);

    // 画像: 画像領域に、クロップ・アスペクト比変更なしで contain。
    // 黒い細枠は画像の実寸にフィットさせて描き、画像と枠線の間に余白（レターボックス）を作らない。
    const iw = img.naturalWidth || img.width;
    const ih = img.naturalHeight || img.height;
    if (!iw || !ih) return;
    const scale = Math.min(cellW / iw, imageAreaH / ih);
    const dw = iw * scale;
    const dh = ih * scale;
    const dx = cx + (cellW - dw) / 2;
    const dy = cy + (imageAreaH - dh) / 2;
    ctx.drawImage(img, dx, dy, dw, dh);
    ctx.strokeStyle = CELL_BORDER_COLOR;
    ctx.lineWidth = CELL_BORDER_WIDTH;
    ctx.strokeRect(dx, dy, dw, dh);

    // === キャプション: 素材名を画像領域の下・セル幅中央に焼き込む（最大 2 行・超過分は省略）===
    if (captionH > 0 && name) {
      ctx.fillStyle = CAPTION_COLOR;
      ctx.font = `600 ${CAPTION_FONT_SIZE}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      let capY = cy + imageAreaH + CAPTION_FONT_SIZE * 0.4;
      for (const line of wrapLines(ctx, name, cellW, 2)) {
        ctx.fillText(line, cx + cellW / 2, capY);
        capY += CAPTION_FONT_SIZE * 1.3;
      }
    }
  });

  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
  cleanup();
  if (!blob) return null;

  return { blob, count: cells.length, sanitizedCount };
}

/**
 * 素材をタイル画像にまとめてブラウザにダウンロードする。
 * 動画生成に添付される統合画像の中身を確認する用途も兼ねるため、options は
 * {@link renderSelectedAssetsTileBlob} にそのまま渡して同じ絵を出せるようにする。
 * @returns 実際にタイルへ配置できた素材枚数（0 の場合は何もダウンロードされない）と、
 *          そのうち覆面化画像で合成した枚数
 */
export async function downloadSelectedAssetsTile(
  assets: GenerationAsset[],
  meta?: { productName?: string; brandName?: string; footerLabel?: string },
  options?: { preferFacePrivacySanitized?: boolean },
): Promise<{ count: number; sanitizedCount: number }> {
  const result = await renderSelectedAssetsTileBlob(assets, meta, options);
  if (!result) return { count: 0, sanitizedCount: 0 };

  const downloadUrl = URL.createObjectURL(result.blob);
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.download = `selected-materials-${result.count}.png`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(downloadUrl);

  return { count: result.count, sanitizedCount: result.sanitizedCount };
}
